import { ClientError } from '../types/diagnostics';

const MAX_ERRORS = 50;
const MAX_STACK_LENGTH = 500;

class ErrorCaptureService {
    private errors: ClientError[] = [];
    private initialized = false;
    private originalConsoleError: ((...args: any[]) => void) | null = null;
    private _isCapturing = false;

    initialize(): void {
        if (this.initialized) return;
        this.initialized = true;

        // Capture uncaught exceptions
        window.onerror = (message, source, lineno, colno, error) => {
            this.addError({
                message: String(message),
                stack: this.sanitizeStack(error?.stack),
                source: source ? `${source}:${lineno}:${colno}` : undefined,
                type: 'unhandled',
                timestamp: new Date().toISOString(),
                currentPage: this.getCurrentPage(),
            });
        };

        // Capture unhandled promise rejections
        window.onunhandledrejection = (event: PromiseRejectionEvent) => {
            const reason = event.reason;
            this.addError({
                message: reason?.message || String(reason),
                stack: this.sanitizeStack(reason?.stack),
                source: undefined,
                type: 'promise',
                timestamp: new Date().toISOString(),
                currentPage: this.getCurrentPage(),
            });
        };

        // Monkey-patch console.error
        this.originalConsoleError = console.error;
        console.error = (...args: any[]) => {
            // Call original first
            this.originalConsoleError?.apply(console, args);

            // Guard against recursion
            if (this._isCapturing) return;
            this._isCapturing = true;

            try {
                const message = args
                    .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
                    .join(' ');

                this.addError({
                    message,
                    stack: undefined,
                    source: undefined,
                    type: 'console',
                    timestamp: new Date().toISOString(),
                    currentPage: this.getCurrentPage(),
                });
            } finally {
                this._isCapturing = false;
            }
        };
    }

    getRecentErrors(): ClientError[] {
        return [...this.errors];
    }

    flushErrors(): ClientError[] {
        const flushed = [...this.errors];
        this.errors = [];
        return flushed;
    }

    private addError(error: ClientError): void {
        this.errors.push(error);
        if (this.errors.length > MAX_ERRORS) {
            this.errors = this.errors.slice(this.errors.length - MAX_ERRORS);
        }
    }

    private getCurrentPage(): string {
        return window.location.pathname + window.location.hash;
    }

    private sanitizeStack(stack?: string): string | undefined {
        if (!stack) return undefined;
        // Strip absolute file-system paths (e.g., C:\Users\... or /home/...)
        let sanitized = stack.replace(/(?:[A-Z]:)?[\\/](?:Users|home)[\\/][^\s:)]+/gi, '<local>');
        if (sanitized.length > MAX_STACK_LENGTH) {
            sanitized = sanitized.substring(0, MAX_STACK_LENGTH);
        }
        return sanitized;
    }
}

export const errorCaptureService = new ErrorCaptureService();
